/* eslint-disable no-debugger */
import { Record, Records, Theme } from './types'

export enum MarkerState {
  defaultSpotlight = 'default-spotlight',
  hoverSpotlight = 'hover-spotlight',
  selectedSpotlight = 'selected-spotlight',
}

export enum MarkerType {
  poi,
  hero,
}

// IFI for the masterlist to fetch only once in runtime
const masterlist = (async () => {
  return (await import('./masterlist.json')) as unknown as Records
})()

const GENERIC_CATEGORY_ID = '350-3500-0233'
const HERO_MARKER_ID = 'light.hero-marker.hero-marker'

export async function getPoiMarkerId(
  categoryId?: string,
  type = MarkerType.poi,
  theme = Theme.light,
  state = MarkerState.defaultSpotlight
): Promise<string | undefined> {
  const records = await masterlist
  switch (type) {
    case MarkerType.hero:
      return HERO_MARKER_ID
    case MarkerType.poi:
    default: {
      const record: Record =
        categoryId !== undefined ? records[categoryId] : records[GENERIC_CATEGORY_ID]

      const assets = record.sprites.assets
      const fileId = record.fileName.replace('.svg', '')

      return assets.length > 0
        ? assets.find((spriteId) => spriteId === `${theme}.pin.${state}.${fileId}`)
        : undefined
    }
  }
}

export enum IconSize {
  large = '24px',
  small = '16px',
}

export enum IconType {
  misc = 'misc',
  poi = 'poi',
  tools = 'tools',
  guidanceIcons = 'guidance-icons',
  travelTransportTracking = 'travel-transport-tracking',
}

export enum IconState {
  outline = 'outline',
  solid = 'solid',
}

export async function getIconId(
  categoryId?: string,
  size = IconSize.small,
  type = IconType.poi,
  state = IconState.solid
): Promise<string | undefined> {
  const records = await masterlist
  const record: Record =
    categoryId !== undefined ? records[categoryId] : records[GENERIC_CATEGORY_ID]
  const icons = record.sprites.icons
  const genericIconography = record.genericIconography
  const iconId = icons.find(
    (spriteId) => spriteId === `${type}.${genericIconography}_${state}_${size}`
  )

  // This control flow covers the case of the generic_point for categories, that they don't map to any icon.
  // Compared to the poi markers which are maped with the generic_point.svg if their category has no a specific icon in the masterlist,
  // the icons don't map with a generic_point inside masterlist. This is done here.
  if (iconId === undefined && categoryId !== undefined) {
    const genericPointIcons = records[GENERIC_CATEGORY_ID].sprites.icons
    const genericPointIconography = records[GENERIC_CATEGORY_ID].genericIconography

    return genericPointIcons.length > 0
      ? genericPointIcons.find(
          (spriteId) => spriteId === `${type}.${genericPointIconography}_${state}_${size}`
        )
      : undefined
  }

  return iconId
}

export enum TokenState {
  default = 'default',
  defaultOutline = 'default-outline',
  spotlight = 'spotlight',
  spotlightOutline = 'spotlight-outline',
}

export async function getColorToken(
  categoryId?: string,
  labelToken?: boolean,
  state = TokenState.default
): Promise<string | undefined> {
  const records = await masterlist
  const record: Record =
    categoryId !== undefined ? records[categoryId] : records[GENERIC_CATEGORY_ID]
  const tokens = record.tokens
  const categoryClass = record.categoryClass
  const label = labelToken !== undefined && labelToken ? '-label' : ''

  return tokens.length > 0
    ? tokens.find((token) => token === `--hds-poi${label}-${categoryClass}-${state}`)
    : undefined
}
